import React, { useEffect, useState } from "react";
import "./Profile.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchMe } from "../../redux/authSlice";
import CertificateGenerator from "./CertificateGenerator";
import Button from "../../components/Button/Button";
import IncapacitiesSection from "./IncapacitiesSection";
import VacationsSection from "./VacationsSection";
import EditProfilePopup from "./EditProfilePopup";

export default function Profile() {
    const dispatch = useDispatch();
    const { employee } = useSelector((state) => state.auth);

    const [profileImgError, setProfileImgError] = useState(false);
    const [emailCopied, setEmailCopied] = useState(false);
    const [activeSection, setActiveSection] = useState("vacaciones");
    const [showEditPopup, setShowEditPopup] = useState(false);

    const employeeName = employee?.name || employee?.fullName || employee?.nickname || "Empleado";
    const employeeNickname = employee?.nickname || "Sin nickname";
    const employeeEmail = employee?.internalEmail || employee?.email || "Sin correo";
    const employeeRole = employee?.role || "sin rol";
    const employeeDescription = employee?.description || employeeRole;
    const employeeImage = employee?.imageProfile || "";
    const employeeInitial = (employeeName || "E").trim().charAt(0).toUpperCase();
    const isConsultant = String(employee?.workday || "").trim().toLowerCase() === "consultant";
    const employeeSalary = employee?.salary || null;
    const employeeHireDate = employee?.hireDate
        ? new Date(employee.hireDate).toLocaleDateString("es-CO", {
            year: "numeric",
            month: "long",
            day: "numeric",
            timeZone: "UTC",
        })
        : "Sin fecha";


    const isInternship = () => {
        if (!employee?.internship?.dateRange) return false;
        const parseIsoDateOnly = (dateValue) => {
            const match = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (!match) return null;
            const year = Number(match[1]);
            const month = Number(match[2]) - 1;
            const day = Number(match[3]);
            const parsed = new Date(year, month, day);
            return Number.isNaN(parsed.getTime()) ? null : parsed;
        };

        const today = new Date();
        today.setHours(0,0,0,0);
        const start = parseIsoDateOnly(employee.internship.dateRange.startDate);
        const end = parseIsoDateOnly(employee.internship.dateRange.endDate);
        if (!start || !end) return false;
        return today >= start && today <= end;
    };

    // Refresca el perfil desde GET /employees/me al entrar.
    useEffect(() => {
        dispatch(fetchMe());
    }, [dispatch]);

    useEffect(() => {
        if (!emailCopied) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setEmailCopied(false);
        }, 2000);

        return () => clearTimeout(timeoutId);
    }, [emailCopied]);

    const handleCopyEmail = async () => {
        if (!employeeEmail || employeeEmail === "Sin correo") {
            return;
        }

        try {
            await navigator.clipboard.writeText(employeeEmail);
        } catch {
            const input = document.createElement("textarea");
            input.value = employeeEmail;
            document.body.appendChild(input);
            input.select();
            document.execCommand("copy");
            document.body.removeChild(input);
        }

        setEmailCopied(true);
    };

    return (
        <article className="container-home container-profile-page">
            <EditProfilePopup isOpen={showEditPopup} onClose={() => setShowEditPopup(false)} />
            <section className="profile-card">
                <div className="profile-avatar-wrap">
                    {employeeImage && !profileImgError ? (
                        <div>
                            <img
                                className="profile-avatar"
                                src={employeeImage}
                                alt={`Foto de ${employeeName}`}
                                onError={() => setProfileImgError(true)}
                            />
                            {isInternship() && <p className="profile-avatar-internship">Internship</p>}
                        </div>
                    ) : (
                        <div className="profile-avatar-fallback">{employeeInitial}</div>
                    )}
                    <div className="profile-avatar-info">
                        <p className="profile-avatar-nick">{employeeNickname}</p>
                        <p className="profile-avatar-email">
                            <button
                                type="button"
                                className="copy-email-btn"
                                onClick={handleCopyEmail}
                                aria-label="Copiar email"
                            >
                                <img src={`${process.env.PUBLIC_URL}/img/icons/copy.svg`} alt="Copiar" />
                            </button>
                            {employeeEmail}
                        </p>
                        {emailCopied && <p className="email-copied-feedback">Email copiado!</p>}
                        {employeeSalary && <p className="profile-avatar-salary">${employeeSalary.toLocaleString('es-CO')} USD</p>}
                        <p className="profile-avatar-role">{employeeHireDate}</p>
                    </div>
                </div>
                <div className="profile-actions">
                    <button className="profile-edit-btn" onClick={() => setShowEditPopup(true)}>Edit Profile</button>
                    <CertificateGenerator
                        employee={employee}
                        employeeName={employeeName}
                        employeeHireDate={employeeHireDate}
                        employeeDescription={employeeDescription}
                    />
                </div>
            </section>

            <div className="divider" aria-hidden="true"></div>

            {!isConsultant && (
                <section className="profile-submenu-section">
                    <div className="profile-submenu" role="tablist" aria-label="Submenu de perfil">
                        <Button
                            type="button"
                            variant="outline"
                            size="small"
                            role="tab"
                            className="profile-submenu-btn"
                            selected={activeSection === "vacaciones"}
                            aria-selected={activeSection === "vacaciones"}
                            onClick={() => setActiveSection("vacaciones")}
                        >
                            Vacaciones
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="small"
                            role="tab"
                            className="profile-submenu-btn"
                            selected={activeSection === "incapacidades"}
                            aria-selected={activeSection === "incapacidades"}
                            onClick={() => setActiveSection("incapacidades")}
                        >
                            Incapacidades
                        </Button>
                    </div>

                    {activeSection === "vacaciones" ? (
                        <VacationsSection />
                    ) : (
                        <IncapacitiesSection />
                    )}
                </section>
            )}
        </article>
    );
}
